"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Upload, FileText, Image, File, X, Loader2, CheckCircle, User, Sparkles, AlertCircle, Shield, Copy, Download, Calendar, ChevronDown, ClipboardList, Brain, ToggleLeft, ToggleRight, FileOutput, PenTool, FileJson, Database } from "lucide-react";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import { cn } from "@/lib/utils";
import { extractTextFromFile, ExtractionResult, DetectedName } from "@/lib/text-extractor";
import { anonymizeText, AnonymizationResult } from "@/lib/anonymizer";
import { useBilanProcessLog, type StepUsage } from "@/lib/bilan-process-logger";
import { isConclusionBuggy as isConclusionBuggyShared, CONCLUSION_LENGTH_OPTIONS, CONCLUSION_LENGTH_LABELS, CONCLUSION_LENGTH_CUSTOM_LABEL, CONCLUSION_LENGTH_CUSTOM_MIN, CONCLUSION_LENGTH_CUSTOM_MAX, type ConclusionLength, type ConclusionLengthChoice } from "@/lib/bilan-conclusion";
import { type ReasoningModelChoice } from "@/lib/reasoning-ai";

interface PatientResult {
  format: string;
  value: string;
  anonymousId?: string;
}

interface PatientIdentification {
  results: PatientResult[];
}

interface BirthdateInfo {
  found: boolean;
  birthdate: string | null;
  birthYear: number | null;
  age: number | null;
  method: "direct" | "age" | "year" | null;
  confidence: "high" | "medium" | "low";
  sourceText: string | null;
}

interface QuestionAnswer {
  selected: string;
  confidence: "high" | "medium" | "low";
  sourceText: string;
}

interface CategoryAnswers {
  category: string;
  answers: Record<string, QuestionAnswer>;
  availableOptions: Record<string, string[]>;
}

interface QuestionnaireData {
  selectedCategories: string[];
  categoryAnswers: Record<string, CategoryAnswers>;
}

interface OrthopticAnalysis {
  success: boolean;
  analysis: string;
  metadata: {
    patientAge: number | null;
    birthdate: string | null;
    rehabilitationCare: boolean;
    hasContext: boolean;
    questionnaireCategories: string[];
    analyzedAt: string;
  };
}

// Génère une chaîne aléatoire pour l'anonymisation
function generateAnonymousId(length: number = 8): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Génère la table de correspondance avec IDs anonymes
function generateCorrespondenceTable(results: PatientResult[]): PatientResult[] {
  // Utiliser un préfixe commun pour le patient
  const patientPrefix = generateAnonymousId(4);

  return results.map((item, index) => ({
    ...item,
    anonymousId: `${patientPrefix}-${String(index + 1).padStart(2, "0")}`,
  }));
}

interface BilanLogUser {
  id?: string;
  name: string;
  email: string;
  image?: string | null;
}

interface FileDropZoneProps {
  onExtraction?: (result: ExtractionResult) => void;
  onPatientIdentified?: (patient: PatientIdentification) => void;
  practitioners?: string[];
  practiceAddress?: string;
  /** Utilisateur connecté (enregistré dans le journal et l'historique) */
  user?: BilanLogUser | null;
  className?: string;
}

export function FileDropZone({
  onExtraction,
  onPatientIdentified,
  practitioners = [],
  practiceAddress = "",
  user = null,
  className
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [identifiedPatient, setIdentifiedPatient] = useState<PatientIdentification | null>(null);
  const [isIdentifying, setIsIdentifying] = useState(false);
  /** Résultat de la recherche patient en base (entre identification et anonymisation) */
  const [patientFromDbResult, setPatientFromDbResult] = useState<{
    found: boolean;
    declensionsCount?: number;
    searchName?: string;
    patientName?: string;
    notesExcerpt?: string;
    declensions?: string[];
  } | null>(null);
  const [anonymizationResult, setAnonymizationResult] = useState<AnonymizationResult | null>(null);
  const [birthdateInfo, setBirthdateInfo] = useState<BirthdateInfo | null>(null);
  const [isExtractingBirthdate, setIsExtractingBirthdate] = useState(false);

  // États pour le questionnaire
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
  const [isAnalyzingQuestionnaire, setIsAnalyzingQuestionnaire] = useState(false);
  const [questionnaireProgress, setQuestionnaireProgress] = useState("");

  // États pour l'analyse orthoptique approfondie
  const [contextNotes, setContextNotes] = useState("");
  const [rehabilitationCare, setRehabilitationCare] = useState(false);
  const [reasoningModel, setReasoningModel] = useState<ReasoningModelChoice>("optionA");
  const [orthopticAnalysis, setOrthopticAnalysis] = useState<OrthopticAnalysis | null>(null);
  const [isAnalyzingOrthoptic, setIsAnalyzingOrthoptic] = useState(false);

  // États pour la conclusion (Vertex + désanonymisation)
  const [conclusionResult, setConclusionResult] = useState<{
    raw: string;  // Conclusion brute avec codes [HUK1-XX]
    final: string | null;  // Conclusion désanonymisée (null = pas encore désanonymisée)
  } | null>(null);
  const [isGeneratingConclusion, setIsGeneratingConclusion] = useState(false);
  const [signature, setSignature] = useState("");
  const [conclusionLength, setConclusionLength] = useState<ConclusionLengthChoice>("moyen");
  const [conclusionLengthCustom, setConclusionLengthCustom] = useState<number>(200);
  const [conclusionLengthConfig, setConclusionLengthConfig] = useState<Record<ConclusionLength, number> | null>(null);

  useEffect(() => {
    fetch("/api/config/conclusion-lengths")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data && typeof data.court === "number" && typeof data.moyen === "number" && typeof data.long === "number") {
          setConclusionLengthConfig({ court: data.court, moyen: data.moyen, long: data.long });
        }
      })
      .catch(() => { });
  }, []);

  // Journal du processus Bilan (logs JSON téléchargeables)
  const processLog = useBilanProcessLog();
  const wasProcessingRef = useRef(false);

  useEffect(() => {
    if (user) {
      processLog.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        image: user.image,
      });
    }
  }, [user?.id, user?.name, user?.email]);

  // Sauvegarder le journal en base à la fin du traitement
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing && processLog.hasSteps) {
      processLog.saveLog();
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, processLog.hasSteps]);

  // États pour les sections rétractables (toutes fermées par défaut)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    names: false,
    patient: false,
    patientDb: false,
    birthdate: false,
    anonymized: false,
    original: false,
    questionnaire: false,
    analysis: true,
    conclusion: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = async (selectedFile: File) => {
    setFile(selectedFile);
    setIsProcessing(true);
    setError(null);
    setResult(null);
    setProgress("Lecture du fichier...");
    processLog.reset(selectedFile.name);

    try {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();

      processLog.startStep("extraction", "Extraction du texte", {
        fileName: selectedFile.name,
        type: selectedFile.type,
        sizeBytes: selectedFile.size,
      });
      if (fileType.startsWith("image/")) {
        setProgress("OCR en cours (peut prendre quelques secondes)...");
      } else if (fileName.endsWith(".pdf")) {
        setProgress("Extraction du PDF...");
      } else if (fileName.endsWith(".docx") || fileName.endsWith(".doc")) {
        setProgress("Extraction du document Word...");
      }

      let extractionResult: ExtractionResult;
      try {
        extractionResult = await extractTextFromFile(selectedFile);
        processLog.endStep("extraction", {
          linesCount: extractionResult.lines.length,
          namesCount: extractionResult.names.length,
          textLength: extractionResult.text.length,
          textSentToNextSteps: extractionResult.text,
        });
      } catch (extractErr) {
        const e = extractErr instanceof Error ? extractErr : new Error(String(extractErr));
        processLog.endStep("extraction", undefined, undefined, { message: e.message, stack: e.stack });
        throw extractErr;
      }

      setProgress("Analyse des noms et prénoms...");
      await new Promise(resolve => setTimeout(resolve, 500)); // Petit délai pour l'UX

      setResult(extractionResult);
      onExtraction?.(extractionResult);

      // Si des noms sont détectés, appeler Gemini pour identifier le patient
      if (extractionResult.names.length > 0) {
        setProgress("Identification du patient avec l'IA...");
        setIsIdentifying(true);

        const identifyPatientBody = {
          detectedNames: extractionResult.names,
          practitioners,
          practiceAddress,
          documentContext: extractionResult.text.substring(0, 2000),
        };
        processLog.startStep("identify-patient", "Identification du patient (IA)", identifyPatientBody);
        try {
          const response = await fetch("/api/identify-patient", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(identifyPatientBody),
          });

          const data = await response.json();
          const usage: StepUsage | undefined = data._usage;
          if (response.ok && data.results) {
            processLog.endStep("identify-patient", data, usage, undefined, { inputPrompt: data._promptSent });
            const resultsWithIds = generateCorrespondenceTable(data.results);
            const patientData = { results: resultsWithIds };
            setIdentifiedPatient(patientData);
            onPatientIdentified?.(patientData);

            // Bloc : recherche patient en base (entre identification et anonymisation)
            // Utiliser seulement les 2 premières parties du nom (ex. nom, prénom) pour éviter une chaîne trop longue (toutes les variantes concaténées ne matchent pas)
            const allParts = (data.results as { value?: string }[])
              .map((r) => r.value?.trim())
              .filter(Boolean) as string[];
            const searchName = allParts.length > 0
              ? allParts.slice(0, 2).join(" ")
              : "";
            processLog.startStep("fetch-patient-db", "Recherche patient en base", { searchName });
            let patientFromDb: { id: string; firstName: string; lastName: string; notes?: string | null; nameDeclensions?: string[] | null } | null = null;
            try {
              if (!searchName) {
                processLog.endStep("fetch-patient-db", { found: false, searchName: "", reason: "Aucun nom à rechercher" });
                setPatientFromDbResult({ found: false, searchName: "" });
              } else {
                const listRes = await fetch(`/api/patients?${new URLSearchParams({ name: searchName })}`);
                const listData = await listRes.json();
                if (listRes.ok && Array.isArray(listData.patients) && listData.patients.length > 0) {
                  const first = listData.patients[0] as { id: string; firstName: string; lastName: string; notes?: string | null; nameDeclensions?: string | string[] | null };
                  const detailRes = await fetch(`/api/patients/${first.id}`);
                  const detail = await detailRes.json();
                  if (detailRes.ok && detail?.id) {
                    patientFromDb = {
                      id: detail.id,
                      firstName: detail.firstName ?? first.firstName,
                      lastName: detail.lastName ?? first.lastName,
                      notes: detail.notes ?? first.notes,
                      nameDeclensions: Array.isArray(detail.nameDeclensions) ? detail.nameDeclensions : typeof detail.nameDeclensions === "string" ? (() => { try { return JSON.parse(detail.nameDeclensions || "[]") as string[]; } catch { return []; } })() : null,
                    };
                  }
                }
                const notesExcerpt = patientFromDb?.notes ? patientFromDb.notes.substring(0, 800) : undefined;
                const declensions = patientFromDb?.nameDeclensions ?? undefined;
                const patientName = patientFromDb ? `${patientFromDb.lastName} ${patientFromDb.firstName}` : undefined;
                processLog.endStep("fetch-patient-db", {
                  found: !!patientFromDb,
                  searchName,
                  patientId: patientFromDb?.id,
                  patientName,
                  summaryLength: patientFromDb?.notes?.length ?? 0,
                  notesExcerpt: notesExcerpt || undefined,
                  declensionsCount: declensions?.length ?? 0,
                  declensions,
                });
                setPatientFromDbResult({
                  found: !!patientFromDb,
                  searchName,
                  patientName,
                  notesExcerpt: notesExcerpt || undefined,
                  declensions,
                  declensionsCount: declensions?.length ?? 0,
                });
              }
            } catch (fetchErr) {
              const e = fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr));
              processLog.endStep("fetch-patient-db", undefined, undefined, { message: e.message, stack: e.stack });
              setPatientFromDbResult({ found: false, searchName });
            }

            processLog.startStep("anonymization", "Anonymisation du document", {
              textLength: extractionResult.text.length,
              correspondenceCount: resultsWithIds.filter((r) => r.value && r.anonymousId).length,
            });
            const baseCorrespondence = resultsWithIds
              .filter((r): r is PatientResult & { value: string; anonymousId: string } => Boolean(r.value && r.anonymousId))
              .map((r) => ({ original: r.value, anonymousId: r.anonymousId as string }));
            const firstPatientCode = baseCorrespondence[0]?.anonymousId;
            const existingOriginals = new Set(baseCorrespondence.map((c: { original: string }) => c.original.toLowerCase()));
            const declensionEntries =
              firstPatientCode && patientFromDb?.nameDeclensions
                ? patientFromDb.nameDeclensions
                  .map((d) => (typeof d === "string" ? d.trim() : ""))
                  .filter((d) => d && !existingOriginals.has(d.toLowerCase()))
                  .map((original) => ({ original, anonymousId: firstPatientCode }))
                : [];
            const correspondence = [...baseCorrespondence, ...declensionEntries];

            const anonymized = anonymizeText(
              extractionResult.text,
              correspondence,
              practitioners
            );
            processLog.endStep("anonymization", {
              anonymizedLength: anonymized.anonymizedText.length,
              replacementsCount: anonymized.replacements?.length ?? 0,
              anonymizedTextReceived: anonymized.anonymizedText,
            });
            setAnonymizationResult(anonymized);

            setProgress("Extraction de la date de naissance...");
            setIsExtractingBirthdate(true);
            const extractBirthdateBody = { anonymizedText: anonymized.anonymizedText };
            processLog.startStep("extract-birthdate", "Extraction date de naissance (IA)", extractBirthdateBody);

            try {
              const birthdateResponse = await fetch("/api/extract-birthdate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(extractBirthdateBody),
              });

              const birthdateData = await birthdateResponse.json();
              const birthUsage: StepUsage | undefined = birthdateData._usage;
              if (birthdateResponse.ok) {
                processLog.endStep("extract-birthdate", birthdateData, birthUsage, undefined, { inputPrompt: birthdateData._promptSent });
                setBirthdateInfo(birthdateData);
              } else {
                processLog.endStep("extract-birthdate", birthdateData, undefined, {
                  message: birthdateData.error || "Erreur API",
                });
              }
            } catch (birthdateError) {
              const e = birthdateError instanceof Error ? birthdateError : new Error(String(birthdateError));
              processLog.endStep("extract-birthdate", undefined, undefined, { message: e.message, stack: e.stack });
              console.error("Erreur extraction date de naissance:", birthdateError);
            } finally {
              setIsExtractingBirthdate(false);
            }

            setProgress("Analyse du questionnaire (extraction en une passe)...");
            setIsAnalyzingQuestionnaire(true);
            const questionnaireBody = { anonymizedText: anonymized.anonymizedText, singlePrompt: true };
            processLog.startStep("questionnaire-single", "Questionnaire - extraction complète", questionnaireBody);

            try {
              const questResponse = await fetch("/api/analyze-questionnaire", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(questionnaireBody),
              });

              const questData = await questResponse.json();
              const questUsage: StepUsage | undefined = questData._usage;
              if (questResponse.ok) {
                processLog.endStep("questionnaire-single", questData, questUsage, undefined, { inputPrompt: questData._promptSent });
                const selectedCategories = questData.selectedCategories || [];
                const categoryAnswers: Record<string, CategoryAnswers> = questData.categoryAnswers || {};
                setQuestionnaireData({
                  selectedCategories,
                  categoryAnswers,
                });
                setQuestionnaireProgress(`${selectedCategories.length} catégories avec données`);
              } else {
                processLog.endStep("questionnaire-single", questData, undefined, {
                  message: questData.error || "Erreur API",
                }, { inputPrompt: questData._promptSent });
              }
            } catch (questError) {
              const e = questError instanceof Error ? questError : new Error(String(questError));
              processLog.endStep("questionnaire-single", undefined, undefined, { message: e.message, stack: e.stack });
              console.error("Erreur analyse questionnaire:", questError);
            } finally {
              setIsAnalyzingQuestionnaire(false);
              setQuestionnaireProgress("");
            }
          } else {
            processLog.endStep("identify-patient", data, undefined, {
              message: data.error || (response.ok ? "Aucun résultat" : "Erreur API"),
            }, { inputPrompt: data._promptSent });
          }
        } catch (aiError) {
          const e = aiError instanceof Error ? aiError : new Error(String(aiError));
          processLog.endStep("identify-patient", undefined, undefined, { message: e.message, stack: e.stack });
          console.error("Erreur identification IA:", aiError);
        } finally {
          setIsIdentifying(false);
        }
      }

      setProgress("");
    } catch (err) {
      console.error("Erreur d'extraction:", err);
      setError(err instanceof Error ? err.message : "Erreur lors de l'extraction");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      await processFile(droppedFile);
    }
  }, [onExtraction]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      await processFile(selectedFile);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setProgress("");
    setIdentifiedPatient(null);
    setAnonymizationResult(null);
    setBirthdateInfo(null);
    setQuestionnaireData(null);
    setQuestionnaireProgress("");
    setContextNotes("");
    setRehabilitationCare(false);
    setOrthopticAnalysis(null);
    setConclusionResult(null);
    setSignature("");
    setConclusionLength("moyen");
    processLog.reset();
  };

  // Lancer l'analyse orthoptique approfondie
  const launchOrthopticAnalysis = async () => {
    if (!anonymizationResult) return;

    const analyzeOrthopticBody = {
      anonymizedText: anonymizationResult.anonymizedText,
      questionnaire: questionnaireData?.categoryAnswers || {},
      patientAge: birthdateInfo?.age || null,
      birthdate: birthdateInfo?.birthdate || null,
      contextNotes,
      rehabilitationCare,
      reasoningModel,
      /** Informations trouvées en base (résumé / notes, déclinaisons) pour enrichir l'analyse */
      patientFromDb: patientFromDbResult?.found
        ? {
          notesExcerpt: patientFromDbResult.notesExcerpt,
          declensionsCount: patientFromDbResult.declensionsCount,
          declensions: patientFromDbResult.declensions,
        }
        : undefined,
    };
    processLog.startStep("analyze-orthoptic", "Analyse orthoptique approfondie (IA)", analyzeOrthopticBody);
    setIsAnalyzingOrthoptic(true);
    setOrthopticAnalysis(null);

    try {
      const response = await fetch("/api/analyze-orthoptic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(analyzeOrthopticBody),
      });

      const data = await response.json();
      if (response.ok) {
        processLog.endStep("analyze-orthoptic", data);
        setOrthopticAnalysis(data);
      } else {
        processLog.endStep("analyze-orthoptic", data, undefined, {
          message: data.error || "Erreur API analyse orthoptique",
        });
        console.error("Erreur API analyse orthoptique");
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      processLog.endStep("analyze-orthoptic", undefined, undefined, { message: e.message, stack: e.stack });
      console.error("Erreur analyse orthoptique:", err);
    } finally {
      setIsAnalyzingOrthoptic(false);
      processLog.saveLog();
    }
  };

  // Générer la conclusion (Vertex) - SANS envoyer les noms réels
  const generateConclusion = async () => {
    if (!orthopticAnalysis?.analysis || !identifiedPatient) return;

    const isChild = birthdateInfo?.age !== null && (birthdateInfo?.age || 0) < 18;
    const patientCodes = identifiedPatient.results
      .filter(r => r.anonymousId)
      .map(r => ({
        format: r.format,
        code: r.anonymousId!,
      }));

    const generateConclusionBody = {
      analysis: orthopticAnalysis.analysis,
      patientAge: birthdateInfo?.age || null,
      patientCodes,
      isChild,
      contextNotes,
      rehabilitationCare,
      signature,
      ...(conclusionLength === "custom"
        ? { targetWords: Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, conclusionLengthCustom)) }
        : { maxLength: conclusionLength }),
    };
    processLog.startStep("generate-conclusion", "Génération de la conclusion (Mistral)", generateConclusionBody);
    setIsGeneratingConclusion(true);
    setConclusionResult(null);

    const callApi = () =>
      fetch("/api/generate-conclusion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(generateConclusionBody),
      });

    try {
      let response = await callApi();
      let data: { conclusion?: string; error?: string; _promptSent?: string } = {};
      try {
        data = await response.json();
      } catch {
        data = { error: "Réponse API invalide" };
      }

      if (response.ok && isConclusionBuggyShared(data.conclusion)) {
        response = await callApi();
        try {
          data = await response.json();
        } catch {
          data = { error: "Réponse API invalide" };
        }
      }

      if (response.ok) {
        processLog.endStep("generate-conclusion", data, undefined, undefined, { inputPrompt: data._promptSent });
        setConclusionResult({
          raw: data.conclusion ?? "",
          final: null,
        });
        setError(null);
      } else {
        const errorMessage = data.error || "Erreur API conclusion";
        processLog.endStep("generate-conclusion", data, undefined, {
          message: errorMessage,
        }, { inputPrompt: data._promptSent });
        setError(errorMessage);
        console.error("Erreur API conclusion:", errorMessage);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      processLog.endStep("generate-conclusion", undefined, undefined, { message: e.message, stack: e.stack });
      setError(e.message);
      console.error("Erreur génération conclusion:", err);
    } finally {
      setIsGeneratingConclusion(false);
      processLog.saveLog();
    }
  };

  // Désanonymiser la conclusion (ÉTAPE LOCALE - aucune donnée envoyée)
  const deanonymizeConclusion = () => {
    if (!conclusionResult?.raw || !identifiedPatient) return;

    processLog.startStep("deanonymize", "Désanonymisation locale (aucune donnée envoyée)", {
      description: "Remplacement des codes [XXX-NN] par le nom du patient, côté client uniquement",
    });
    const isChild = birthdateInfo?.age !== null && (birthdateInfo?.age || 0) < 18;

    // Table de correspondance complète (avec valeurs réelles)
    const patientFormats = identifiedPatient.results
      .filter(r => r.anonymousId)
      .map(r => ({
        format: r.format.toUpperCase(), // Normaliser en majuscules
        code: r.anonymousId!,
        value: r.value,
      }));

    let finalConclusion = conclusionResult.raw;

    // Trouver le format par défaut selon l'âge (recherche flexible)
    const defaultFormat = patientFormats.find(pf => {
      const fmt = pf.format.toUpperCase();
      if (isChild) {
        // Pour enfant : chercher PRÉNOM seul
        return fmt === "PRÉNOM" || fmt === "PRENOM";
      } else {
        // Pour adulte : chercher NOM PRÉNOM
        return fmt === "NOM PRÉNOM" || fmt === "NOM PRENOM";
      }
    }) || patientFormats[0];

    console.log("Désanonymisation - isChild:", isChild, "defaultFormat:", defaultFormat);

    // Remplacer tous les codes par le nom approprié
    for (const pf of patientFormats) {
      const codePattern = new RegExp(`\\[${pf.code}\\]`, "gi");
      finalConclusion = finalConclusion.replace(codePattern, defaultFormat?.value || pf.value);
    }

    // Remplacer "le patient" / "la patiente" si encore présent
    if (defaultFormat) {
      finalConclusion = finalConclusion.replace(/\ble patient\b/gi, defaultFormat.value);
      finalConclusion = finalConclusion.replace(/\bla patiente\b/gi, defaultFormat.value);
    }

    // Remplacer "[Votre Nom]" par la signature
    finalConclusion = finalConclusion.replace(/\[Votre Nom\]/gi, signature || "L'orthoptiste");

    // Remplacer [Prénom], [Nom] si le modèle n'a pas utilisé les bons codes
    finalConclusion = finalConclusion.replace(/\[Prénom\]/gi, defaultFormat?.value || "");
    finalConclusion = finalConclusion.replace(/\[Nom\]/gi, defaultFormat?.value || "");

    setConclusionResult({
      raw: conclusionResult.raw,
      final: finalConclusion,
    });
    processLog.endStep("deanonymize", {
      description: "Conclusion désanonymisée côté client",
      localOnly: true,
      finalLength: finalConclusion.length,
      finalConclusion,
    });
    processLog.saveLog();
  };

  // Mettre à jour une réponse du questionnaire
  const updateQuestionnaireAnswer = (category: string, question: string, newValue: string) => {
    if (!questionnaireData) return;

    setQuestionnaireData(prev => {
      if (!prev) return prev;

      const updatedCategoryAnswers = { ...prev.categoryAnswers };
      if (updatedCategoryAnswers[category]?.answers[question]) {
        updatedCategoryAnswers[category] = {
          ...updatedCategoryAnswers[category],
          answers: {
            ...updatedCategoryAnswers[category].answers,
            [question]: {
              ...updatedCategoryAnswers[category].answers[question],
              selected: newValue,
              confidence: "high" as const, // L'utilisateur a modifié = haute confiance
            }
          }
        };
      }

      return {
        ...prev,
        categoryAnswers: updatedCategoryAnswers
      };
    });
  };

  const copyAnonymizedText = () => {
    if (anonymizationResult) {
      navigator.clipboard.writeText(anonymizationResult.anonymizedText);
    }
  };

  const downloadAnonymizedText = () => {
    if (anonymizationResult && file) {
      const blob = new Blob([anonymizationResult.anonymizedText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anonymized_${file.name.replace(/\.[^/.]+$/, "")}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  /** Nom de fichier pour le document complet : NOM_Prénom_DD_MM_YYYY.pdf / .docx */
  const getBilanDownloadFilename = (extension: "pdf" | "docx"): string => {
    const nomRaw = identifiedPatient?.results?.find(
      (r) => r.format === "NOM" || r.format?.toUpperCase() === "NOM"
    )?.value?.trim();
    const prenomRaw = identifiedPatient?.results?.find(
      (r) =>
        r.format === "Prénom" ||
        r.format === "PRÉNOM" ||
        r.format?.toUpperCase() === "PRENOM"
    )?.value?.trim();
    const nom = nomRaw ? nomRaw.toUpperCase().replace(/\s+/g, "_") : "";
    const prenom = prenomRaw
      ? prenomRaw.charAt(0).toUpperCase() + prenomRaw.slice(1).toLowerCase().replace(/\s+/g, "_")
      : "";
    const dateStr = birthdateInfo?.birthdate?.replace(/\//g, "_") ?? "";
    if (nom && prenom && dateStr) {
      return `${nom}_${prenom}_${dateStr}.${extension}`;
    }
    return `bilan_complet_${new Date().toISOString().split("T")[0]}.${extension}`;
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) return Image;
    if (file.name.endsWith(".pdf")) return FileText;
    return File;
  };

  const getConfidenceBadge = (confidence: "high" | "medium" | "low") => {
    switch (confidence) {
      case "high":
        return <span className="badge-success text-xs">Haute</span>;
      case "medium":
        return <span className="badge-warning text-xs">Moyenne</span>;
      case "low":
        return <span className="badge-neutral text-xs">Faible</span>;
    }
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-3xl p-8 md:p-12 text-center transition-all duration-300",
          isDragging
            ? "border-primary-500 bg-primary-50/50 scale-[1.02]"
            : "border-slate-300 bg-white/30 hover:border-primary-400 hover:bg-white/50",
          isProcessing && "pointer-events-none opacity-70"
        )}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx,image/*"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />

        {isProcessing ? (
          <div className="space-y-4">
            <Loader2 className="h-12 w-12 mx-auto text-primary-500 animate-spin" />
            <p className="text-sm font-semibold text-slate-700">{progress}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className={cn(
                "p-4 rounded-2xl transition-all duration-300",
                isDragging
                  ? "bg-primary-100 text-primary-600"
                  : "bg-slate-100 text-slate-500"
              )}>
                <Upload className="h-10 w-10" />
              </div>
            </div>
            <div>
              <p className="text-base font-semibold text-slate-700">
                Glissez-déposez un fichier ici
              </p>
              <p className="text-sm text-slate-500 mt-1">
                ou cliquez pour sélectionner
              </p>
            </div>
            <div className="flex justify-center gap-2 flex-wrap">
              <span className="badge-primary">PDF</span>
              <span className="badge-primary">Word (.doc/.docx)</span>
              <span className="badge-primary">Images</span>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-error-50/80 border-error-200">
          <p className="text-sm font-semibold text-error-700">{error}</p>
        </div>
      )}

      {/* File Info */}
      {file && !isProcessing && (
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {(() => {
                const IconComponent = getFileIcon(file);
                return (
                  <div className="p-2 rounded-xl bg-primary-100 text-primary-600">
                    <IconComponent className="h-5 w-5" />
                  </div>
                );
              })()}
              <div>
                <p className="text-sm font-semibold text-slate-900 truncate max-w-[200px] md:max-w-none">
                  {file.name}
                </p>
                <p className="text-xs text-slate-500">
                  {(file.size / 1024).toFixed(1)} Ko
                </p>
              </div>
            </div>
            <button
              onClick={clearFile}
              className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-primary-600">{result.lines.length}</p>
              <p className="text-xs font-semibold text-slate-500">Lignes extraites</p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-secondary-600">{result.names.length}</p>
              <p className="text-xs font-semibold text-slate-500">Noms détectés</p>
            </div>
            <div className="card p-4 text-center col-span-2 md:col-span-1">
              <p className="text-2xl font-bold text-success-600">
                {result.names.filter(n => n.confidence === "high").length}
              </p>
              <p className="text-xs font-semibold text-slate-500">Haute confiance</p>
            </div>
          </div>

          {/* Detected Names */}
          {result.names.length > 0 && (
            <div className="card overflow-hidden">
              <button
                onClick={() => toggleSection("names")}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-bold text-slate-900">Noms détectés</h3>
                  <span className="badge-neutral text-xs">{result.names.length}</span>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 text-slate-400 transition-transform duration-200",
                  expandedSections.names && "rotate-180"
                )} />
              </button>
              {expandedSections.names && (
                <div className="px-6 pb-6 space-y-3 max-h-80 overflow-y-auto">
                  {result.names.map((name, index) => (
                    <div
                      key={index}
                      className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 hover:shadow-md transition-all"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900">{name.fullName}</p>
                        <p className="text-xs text-slate-500 truncate">
                          Ligne {name.lineNumber}: {name.line.substring(0, 60)}...
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getConfidenceBadge(name.confidence)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AI Patient Identification Results */}
          {(isIdentifying || identifiedPatient) && (
            <div className="card overflow-hidden border-2 border-primary-200 bg-gradient-to-br from-primary-50/50 to-white">
              <button
                onClick={() => !isIdentifying && toggleSection("patient")}
                className="w-full p-4 flex items-center justify-between hover:bg-primary-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "p-2 rounded-xl",
                    isIdentifying ? "bg-primary-100 text-primary-600" : "bg-primary-100 text-primary-600"
                  )}>
                    {isIdentifying ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {isIdentifying ? "Analyse IA en cours..." : "Patient identifié"}
                  </h3>
                  {identifiedPatient && (
                    <span className="badge-primary text-xs">{identifiedPatient.results.length} variantes</span>
                  )}
                </div>
                {!isIdentifying && (
                  <ChevronDown className={cn(
                    "h-5 w-5 text-slate-400 transition-transform duration-200",
                    expandedSections.patient && "rotate-180"
                  )} />
                )}
              </button>

              {expandedSections.patient && identifiedPatient && identifiedPatient.results && (
                <div className="px-6 pb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {identifiedPatient.results.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-slate-200/50 hover:shadow-md hover:border-primary-300 transition-all group"
                      >
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">
                          {item.format}
                        </span>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-base font-semibold text-slate-900 group-hover:text-primary-600 transition-colors">
                            {item.value}
                          </span>
                          {item.anonymousId && (
                            <span className="text-xs font-mono px-2 py-1 rounded-lg bg-slate-100 text-slate-500 border border-slate-200">
                              [{item.anonymousId}]
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Table de correspondance : variante → code (même principe [code] pour l'anonymisation) */}
                  <div className="mt-6 p-4 rounded-2xl bg-slate-900 text-sm">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                        Correspondance variante → code (dans le document : [code])
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const table = identifiedPatient.results.reduce((acc, item) => {
                            if (item.value && item.anonymousId) {
                              acc[item.value] = item.anonymousId;
                            }
                            return acc;
                          }, {} as Record<string, string>);
                          navigator.clipboard.writeText(JSON.stringify(table, null, 2));
                        }}
                        className="text-xs px-3 py-1 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
                      >
                        Copier JSON
                      </button>
                    </div>
                    <p className="text-slate-500 text-xs mb-2">
                      Chaque variante du nom est remplacée par son code entre crochets dans le texte anonymisé (ex. [01Z4-01]).
                    </p>
                    <pre className="text-green-400 font-mono text-xs overflow-x-auto">
                      {JSON.stringify(
                        identifiedPatient.results.reduce((acc, item) => {
                          if (item.value && item.anonymousId) {
                            acc[item.value] = item.anonymousId;
                          }
                          return acc;
                        }, {} as Record<string, string>),
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recherche patient en base (entre identification et anonymisation) */}
          {identifiedPatient && (
            <div className="card overflow-hidden border-2 border-slate-200 bg-gradient-to-br from-slate-50/50 to-white">
              <button
                onClick={() => toggleSection("patientDb")}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-slate-100 text-slate-600">
                    <Database className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Recherche patient en base</h3>
                  {patientFromDbResult === null ? (
                    <span className="text-xs text-slate-500">En cours...</span>
                  ) : patientFromDbResult.found ? (
                    <span className="badge-success text-xs">
                      Patient trouvé{patientFromDbResult.declensionsCount != null && patientFromDbResult.declensionsCount > 0 ? ` · ${patientFromDbResult.declensionsCount} déclinaisons` : ""}
                    </span>
                  ) : (
                    <span className="badge text-xs bg-slate-200 text-slate-600">Aucun en base</span>
                  )}
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 text-slate-400 transition-transform duration-200",
                  expandedSections.patientDb && "rotate-180"
                )} />
              </button>
              {expandedSections.patientDb && (
                <div className="px-6 pb-6 space-y-4">
                  {patientFromDbResult?.searchName != null && patientFromDbResult.searchName !== "" && (
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Nom utilisé pour la recherche</p>
                      <p className="text-sm font-semibold text-slate-900">{patientFromDbResult.searchName}</p>
                    </div>
                  )}
                  {patientFromDbResult?.found && patientFromDbResult.patientName && (
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Patient trouvé en base</p>
                      <p className="text-sm font-semibold text-primary-600">{patientFromDbResult.patientName}</p>
                    </div>
                  )}
                  {patientFromDbResult?.found && patientFromDbResult.notesExcerpt && (
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Résumé / notes (extrait)</p>
                      <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {patientFromDbResult.notesExcerpt}
                        {(patientFromDbResult.notesExcerpt?.length ?? 0) >= 800 && "…"}
                      </div>
                    </div>
                  )}
                  {patientFromDbResult?.found && Array.isArray(patientFromDbResult.declensions) && patientFromDbResult.declensions.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Déclinaisons du nom utilisées pour l'anonymisation</p>
                      <div className="flex flex-wrap gap-2">
                        {patientFromDbResult.declensions.map((d: string, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-700 border border-slate-200">
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {patientFromDbResult === null && (
                    <p className="text-sm text-slate-600">Recherche du patient dans la base (page Patients) par nom, pour récupérer le résumé et les déclinaisons utilisées dans l'anonymisation.</p>
                  )}
                  {patientFromDbResult?.found && !patientFromDbResult.notesExcerpt && !(patientFromDbResult.declensions?.length) && (
                    <p className="text-sm text-slate-600">Le patient a été trouvé. Son résumé et ses déclinaisons ont été utilisés pour enrichir l'anonymisation.</p>
                  )}
                  {patientFromDbResult?.found === false && (
                    <p className="text-sm text-slate-600">Aucun patient correspondant n'a été trouvé dans la base pour ce nom. L'anonymisation utilise uniquement les variantes identifiées par l'IA.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Birthdate Extraction Results */}
          {(isExtractingBirthdate || birthdateInfo) && (
            <div className="card overflow-hidden border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
              <button
                onClick={() => !isExtractingBirthdate && toggleSection("birthdate")}
                className="w-full p-4 flex items-center justify-between hover:bg-amber-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "p-2 rounded-xl",
                    isExtractingBirthdate ? "bg-amber-100 text-amber-600" : "bg-amber-100 text-amber-600"
                  )}>
                    {isExtractingBirthdate ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Calendar className="h-5 w-5" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {isExtractingBirthdate ? "Extraction de la date de naissance..." : "Date de naissance"}
                  </h3>
                  {birthdateInfo && birthdateInfo.found && birthdateInfo.birthdate && (
                    <span className="badge-warning text-xs">{birthdateInfo.birthdate}</span>
                  )}
                </div>
                {!isExtractingBirthdate && (
                  <ChevronDown className={cn(
                    "h-5 w-5 text-slate-400 transition-transform duration-200",
                    expandedSections.birthdate && "rotate-180"
                  )} />
                )}
              </button>

              {expandedSections.birthdate && birthdateInfo && (
                <div className="px-6 pb-6 space-y-4">
                  {birthdateInfo.found ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {birthdateInfo.birthdate && (
                          <div className="p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-amber-200/50">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                              Date de naissance
                            </span>
                            <p className="text-xl font-bold text-slate-900 mt-1">
                              {birthdateInfo.birthdate}
                            </p>
                          </div>
                        )}
                        {birthdateInfo.birthYear && (
                          <div className="p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-amber-200/50">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                              Année de naissance
                            </span>
                            <p className="text-xl font-bold text-slate-900 mt-1">
                              {birthdateInfo.birthYear}
                            </p>
                          </div>
                        )}
                        {birthdateInfo.age && (
                          <div className="p-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-amber-200/50">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                              Âge
                            </span>
                            <p className="text-xl font-bold text-slate-900 mt-1">
                              {birthdateInfo.age} ans
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-sm">
                        <span className={cn(
                          "px-2 py-1 rounded-lg text-xs font-medium",
                          birthdateInfo.confidence === "high" && "bg-success-100 text-success-700",
                          birthdateInfo.confidence === "medium" && "bg-amber-100 text-amber-700",
                          birthdateInfo.confidence === "low" && "bg-slate-100 text-slate-600"
                        )}>
                          Confiance : {birthdateInfo.confidence === "high" ? "Haute" : birthdateInfo.confidence === "medium" ? "Moyenne" : "Faible"}
                        </span>
                        <span className="text-slate-500">
                          Méthode : {birthdateInfo.method === "direct" ? "Date directe" : birthdateInfo.method === "age" ? "Calcul via âge" : "Année mentionnée"}
                        </span>
                      </div>

                      {birthdateInfo.sourceText && (
                        <div className="p-3 rounded-xl bg-slate-100 text-xs text-slate-600 italic">
                          "{birthdateInfo.sourceText}"
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-500">
                      <AlertCircle className="h-5 w-5" />
                      <span>Aucune date de naissance trouvée dans le document</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Anonymization Results */}
          {anonymizationResult && (
            <div className="card overflow-hidden border-2 border-success-200 bg-gradient-to-br from-success-50/50 to-white">
              <button
                onClick={() => toggleSection("anonymized")}
                className="w-full p-4 flex items-center justify-between hover:bg-success-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-success-100 text-success-600">
                    <Shield className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Document anonymisé</h3>
                  <span className="badge-success text-xs">{anonymizationResult.replacements.length} types</span>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 text-slate-400 transition-transform duration-200",
                  expandedSections.anonymized && "rotate-180"
                )} />
              </button>

              {expandedSections.anonymized && (
                <div className="px-6 pb-6">
                  {/* Résumé des remplacements */}
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-slate-700 mb-2">Remplacements effectués :</h4>
                    <div className="flex flex-wrap gap-2">
                      {anonymizationResult.replacements.map((r, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs"
                        >
                          <span className="font-bold text-slate-600">{r.type}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono text-success-600">{r.replacement}</span>
                          <span className="text-slate-400">×{r.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); copyAnonymizedText(); }}
                      className="flex-1 btn-secondary flex items-center justify-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Copier
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadAnonymizedText(); }}
                      className="flex-1 btn-primary flex items-center justify-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                  </div>

                  {/* Texte anonymisé */}
                  <div className="max-h-80 overflow-y-auto rounded-xl bg-slate-900 p-4 font-mono text-xs text-slate-300 whitespace-pre-wrap">
                    {anonymizationResult.anonymizedText.substring(0, 5000)}
                    {anonymizationResult.anonymizedText.length > 5000 && (
                      <span className="text-slate-500">... (tronqué)</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Questionnaire Results */}
          {(isAnalyzingQuestionnaire || questionnaireData) && (
            <div className="card overflow-hidden border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white">
              <button
                onClick={() => !isAnalyzingQuestionnaire && toggleSection("questionnaire")}
                className="w-full p-4 flex items-center justify-between hover:bg-indigo-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "p-2 rounded-xl",
                    isAnalyzingQuestionnaire ? "bg-indigo-100 text-indigo-600" : "bg-indigo-100 text-indigo-600"
                  )}>
                    {isAnalyzingQuestionnaire ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <ClipboardList className="h-5 w-5" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {isAnalyzingQuestionnaire ? "Analyse du questionnaire..." : "Questionnaire pré-rempli"}
                  </h3>
                  {questionnaireData && (
                    <span className="badge-primary text-xs">
                      {questionnaireData.selectedCategories.length} catégories
                    </span>
                  )}
                  {isAnalyzingQuestionnaire && questionnaireProgress && (
                    <span className="text-xs text-indigo-600">{questionnaireProgress}</span>
                  )}
                </div>
                {!isAnalyzingQuestionnaire && (
                  <ChevronDown className={cn(
                    "h-5 w-5 text-slate-400 transition-transform duration-200",
                    expandedSections.questionnaire && "rotate-180"
                  )} />
                )}
              </button>

              {expandedSections.questionnaire && questionnaireData && (
                <div className="px-6 pb-6 space-y-6">
                  {questionnaireData.selectedCategories.map((category) => {
                    const catData = questionnaireData.categoryAnswers[category];
                    if (!catData) return null;
                    const answersWithCitation = Object.entries(catData.answers).filter(
                      ([, answer]) => answer.sourceText != null && String(answer.sourceText).trim() !== ""
                    );
                    if (answersWithCitation.length === 0) return null;

                    return (
                      <div key={category} className="space-y-3">
                        <h4 className="text-sm font-bold text-indigo-700 uppercase tracking-wide border-b border-indigo-200 pb-2">
                          {category.replace(/_/g, " ")}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {answersWithCitation.map(([question, answer], idx) => {
                            const options = catData.availableOptions[question] || [];
                            const keyLooksLikeCitation =
                              question.trim().startsWith("- ") ||
                              question.length > 70 ||
                              (answer.sourceText != null && answer.sourceText.trim() === question.trim()) ||
                              /\d\/10|ppc|rdc|:\s*\d|cm\b|d\'?\d|d\d\s|c\d|^avl\s|avp\s|exophorie|strabisme\s*:/i.test(question);
                            const questionTitle =
                              !keyLooksLikeCitation && !/^\d+$/.test(question) ? question : `Question ${idx + 1}`;
                            return (
                              <div
                                key={question}
                                className="p-4 rounded-xl bg-white/80 border border-slate-200 hover:border-indigo-300 transition-all"
                              >
                                <label className="block text-xs font-bold text-slate-600 mb-2">
                                  {questionTitle}
                                </label>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={answer.selected}
                                    onChange={(e) => updateQuestionnaireAnswer(category, question, e.target.value)}
                                    className={cn(
                                      "flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all",
                                      "focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                      answer.confidence === "high" && "border-success-300 bg-success-50",
                                      answer.confidence === "medium" && "border-amber-300 bg-amber-50",
                                      answer.confidence === "low" && "border-slate-300 bg-slate-50"
                                    )}
                                  >
                                    {options.map((opt) => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                  <div className={cn(
                                    "w-2 h-2 rounded-full flex-shrink-0",
                                    answer.confidence === "high" && "bg-success-500",
                                    answer.confidence === "medium" && "bg-amber-500",
                                    answer.confidence === "low" && "bg-slate-400"
                                  )} title={`Confiance: ${answer.confidence}`} />
                                </div>
                                {answer.sourceText && (
                                  <p className="mt-2 text-xs text-slate-500 italic truncate" title={answer.sourceText}>
                                    "{answer.sourceText.substring(0, 80)}..."
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Export JSON */}
                  <div className="pt-4 border-t border-indigo-200">
                    <button
                      onClick={() => {
                        const exportData = {
                          categories: questionnaireData.categoryAnswers,
                          exportedAt: new Date().toISOString(),
                        };
                        navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
                      }}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Copier le questionnaire (JSON)
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analyse Orthoptique Approfondie */}
          {anonymizationResult && (
            <div className="card overflow-hidden border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
              <button
                onClick={() => toggleSection("analysis")}
                className="w-full p-4 flex items-center justify-between hover:bg-purple-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-purple-100 text-purple-600">
                    <Brain className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Analyse Orthoptique Approfondie</h3>
                  {orthopticAnalysis && (
                    <span className="badge-success text-xs">Générée</span>
                  )}
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 text-slate-400 transition-transform duration-200",
                  expandedSections.analysis && "rotate-180"
                )} />
              </button>

              {expandedSections.analysis && (
                <div className="px-6 pb-6 space-y-6">
                  {/* Zone de contexte */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Contexte et mots-clés (optionnel)
                    </label>
                    <textarea
                      value={contextNotes}
                      onChange={(e) => setContextNotes(e.target.value)}
                      placeholder="Ex: RDV de suivi dans 3 mois, amélioration depuis le dernier bilan, patient anxieux..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Ces informations seront intégrées au début de l'analyse
                    </p>
                  </div>

                  {/* Toggle Rééducation */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/80 border border-slate-200">
                    <div>
                      <p className="font-bold text-slate-900">Prise en charge de rééducation</p>
                      <p className="text-xs text-slate-500">
                        {rehabilitationCare
                          ? "L'IA pourra proposer une rééducation si nécessaire"
                          : "L'IA ne proposera PAS de rééducation"}
                      </p>
                    </div>
                    <button
                      onClick={() => setRehabilitationCare(!rehabilitationCare)}
                      className={cn(
                        "p-2 rounded-xl transition-all",
                        rehabilitationCare
                          ? "bg-success-100 text-success-600"
                          : "bg-slate-100 text-slate-400"
                      )}
                    >
                      {rehabilitationCare ? (
                        <ToggleRight className="h-8 w-8" />
                      ) : (
                        <ToggleLeft className="h-8 w-8" />
                      )}
                    </button>
                  </div>

                  {/* Choix du modèle de raisonnement */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Modèle pour l'analyse (raisonnement)
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <label className={cn(
                        "flex items-center gap-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all",
                        reasoningModel === "optionA"
                          ? "border-purple-500 bg-purple-50 text-purple-900"
                          : "border-slate-200 bg-white/80 text-slate-600 hover:border-slate-300"
                      )}>
                        <input
                          type="radio"
                          name="reasoningModel"
                          value="optionA"
                          checked={reasoningModel === "optionA"}
                          onChange={() => setReasoningModel("optionA")}
                          className="sr-only"
                        />
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm">Option A</span>
                          <span className="text-[10px] opacity-70">Gemini 2.5 reasoning</span>
                        </div>
                      </label>
                      <label className={cn(
                        "flex items-center gap-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all",
                        reasoningModel === "optionB"
                          ? "border-purple-500 bg-purple-50 text-purple-900"
                          : "border-slate-200 bg-white/80 text-slate-600 hover:border-slate-300"
                      )}>
                        <input
                          type="radio"
                          name="reasoningModel"
                          value="optionB"
                          checked={reasoningModel === "optionB"}
                          onChange={() => setReasoningModel("optionB")}
                          className="sr-only"
                        />
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm">Option B</span>
                          <span className="text-[10px] opacity-70">5.1-mini mode reasoning</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Résumé des données */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-xl bg-slate-50 text-center">
                      <p className="text-xs text-slate-500">Âge</p>
                      <p className="font-bold text-slate-900">
                        {birthdateInfo?.age ? `${birthdateInfo.age} ans` : "—"}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 text-center">
                      <p className="text-xs text-slate-500">Naissance</p>
                      <p className="font-bold text-slate-900">
                        {birthdateInfo?.birthdate || "—"}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 text-center">
                      <p className="text-xs text-slate-500">Questionnaire</p>
                      <p className="font-bold text-slate-900">
                        {questionnaireData?.selectedCategories.length || 0} cat.
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 text-center">
                      <p className="text-xs text-slate-500">Rééducation</p>
                      <p className={cn(
                        "font-bold",
                        rehabilitationCare ? "text-success-600" : "text-slate-400"
                      )}>
                        {rehabilitationCare ? "Oui" : "Non"}
                      </p>
                    </div>
                  </div>

                  {/* Bouton Lancer l'analyse */}
                  <button
                    onClick={launchOrthopticAnalysis}
                    disabled={isAnalyzingOrthoptic}
                    className={cn(
                      "w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-3",
                      isAnalyzingOrthoptic
                        ? "bg-purple-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl"
                    )}
                  >
                    {isAnalyzingOrthoptic ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Analyse en cours... (peut prendre quelques secondes)
                      </>
                    ) : (
                      <>
                        <Brain className="h-5 w-5" />
                        Lancer l'analyse orthoptique approfondie
                      </>
                    )}
                  </button>

                  {/* Résultat de l'analyse */}
                  {orthopticAnalysis && orthopticAnalysis.success && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-slate-900">Résultat de l'analyse</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigator.clipboard.writeText(orthopticAnalysis.analysis)}
                            className="btn-secondary text-xs flex items-center gap-1"
                          >
                            <Copy className="h-3 w-3" />
                            Copier
                          </button>
                          <button
                            onClick={() => {
                              const blob = new Blob([orthopticAnalysis.analysis], { type: "text/plain" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `analyse_orthoptique_${new Date().toISOString().split("T")[0]}.txt`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="btn-primary text-xs flex items-center gap-1"
                          >
                            <FileOutput className="h-3 w-3" />
                            Télécharger
                          </button>
                        </div>
                      </div>

                      <div className="p-6 rounded-xl bg-white border border-purple-200 prose prose-sm max-w-none">
                        <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                          {orthopticAnalysis.analysis}
                        </div>
                      </div>

                      <p className="text-xs text-slate-400 text-right">
                        Généré le {new Date(orthopticAnalysis.metadata.analyzedAt).toLocaleString("fr-FR")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Conclusion Orthoptique (Vertex + Désanonymisation) */}
          {orthopticAnalysis && orthopticAnalysis.success && (
            <div className="card overflow-hidden border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
              <button
                onClick={() => toggleSection("conclusion")}
                className="w-full p-4 flex items-center justify-between hover:bg-emerald-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-emerald-100 text-emerald-600">
                    <PenTool className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Conclusion Orthoptique</h3>
                  {conclusionResult && (
                    <span className="badge-success text-xs">Prête</span>
                  )}
                  <span className="text-xs text-slate-400 ml-2">IA</span>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 text-slate-400 transition-transform duration-200",
                  expandedSections.conclusion && "rotate-180"
                )} />
              </button>

              {expandedSections.conclusion && (
                <div className="px-6 pb-6 space-y-4">
                  {/* Info patient */}
                  <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-200">
                    <p className="text-sm text-slate-600">
                      <strong>Patient :</strong> {identifiedPatient?.results[0]?.value || "Non identifié"}
                      {birthdateInfo?.age && (
                        <span className="ml-2 text-xs text-slate-500">
                          ({birthdateInfo.age < 18 ? `Enfant - prénom utilisé` : `Adulte - nom complet`})
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Signature */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Signature de l'orthoptiste
                    </label>
                    <input
                      type="text"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="Ex: Dr. Martin DUPONT, Orthoptiste"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white/80 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>

                  {/* Longueur */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Longueur de la conclusion
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {CONCLUSION_LENGTH_OPTIONS.map((length: ConclusionLength) => (
                        <button
                          key={length}
                          onClick={() => setConclusionLength(length)}
                          className={cn(
                            "flex-1 min-w-[80px] py-2 px-4 rounded-xl text-sm font-medium transition-all",
                            conclusionLength === length
                              ? "bg-emerald-500 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          )}
                        >
                          {CONCLUSION_LENGTH_LABELS[length]}
                        </button>
                      ))}
                      <button
                        onClick={() => setConclusionLength("custom")}
                        className={cn(
                          "flex-1 min-w-[80px] py-2 px-4 rounded-xl text-sm font-medium transition-all",
                          conclusionLength === "custom"
                            ? "bg-emerald-500 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        )}
                      >
                        {CONCLUSION_LENGTH_CUSTOM_LABEL}
                      </button>
                    </div>
                    {conclusionLength === "custom" && (
                      <div className="mt-2 flex items-center gap-2">
                        <label htmlFor="conclusion-custom-chars-drop" className="text-sm text-slate-600">Cible (mots) :</label>
                        <input
                          id="conclusion-custom-chars-drop"
                          type="number"
                          min={CONCLUSION_LENGTH_CUSTOM_MIN}
                          max={CONCLUSION_LENGTH_CUSTOM_MAX}
                          value={conclusionLengthCustom}
                          onChange={(e) => setConclusionLengthCustom(Math.max(CONCLUSION_LENGTH_CUSTOM_MIN, Math.min(CONCLUSION_LENGTH_CUSTOM_MAX, parseInt(e.target.value, 10) || CONCLUSION_LENGTH_CUSTOM_MIN)))}
                          className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Bouton Générer */}
                  <button
                    onClick={generateConclusion}
                    disabled={isGeneratingConclusion || !identifiedPatient}
                    className={cn(
                      "w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-3",
                      isGeneratingConclusion
                        ? "bg-emerald-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg hover:shadow-xl"
                    )}
                  >
                    {isGeneratingConclusion ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <PenTool className="h-5 w-5" />
                        Générer la conclusion
                      </>
                    )}
                  </button>

                  {/* Résultat - Étape 1 : Conclusion brute (avec codes) */}
                  {conclusionResult && (
                    <div className="space-y-4">
                      {/* Conclusion brute avec codes anonymes */}
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs font-bold rounded">ÉTAPE 1</span>
                            <h4 className="font-bold text-slate-700">Conclusion générée (anonyme)</h4>
                          </div>
                          <span className="badge-success text-xs">OK</span>
                        </div>
                        <div className="p-4 rounded-lg bg-white border border-slate-200 whitespace-pre-wrap text-sm text-slate-600 max-h-40 overflow-y-auto">
                          {conclusionResult.raw}
                        </div>
                        <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          Les codes [HUK1-XX] préservent l'anonymat du patient
                        </p>
                      </div>

                      {/* Étape 2 : Désanonymisation */}
                      {!conclusionResult.final ? (
                        <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-2 py-1 bg-amber-200 text-amber-700 text-xs font-bold rounded">ÉTAPE 2</span>
                            <h4 className="font-bold text-amber-800">Désanonymisation locale</h4>
                          </div>
                          <p className="text-sm text-amber-700 mb-4">
                            Cette opération remplace les codes par les vrais noms.
                            <br />
                            <strong>100% local</strong> - aucune donnée n'est envoyée.
                          </p>
                          <button
                            onClick={deanonymizeConclusion}
                            className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                          >
                            <User className="h-5 w-5" />
                            Désanonymiser la conclusion
                          </button>
                        </div>
                      ) : (
                        /* Conclusion finale désanonymisée */
                        <div className="space-y-4">
                          <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-300">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-emerald-200 text-emerald-700 text-xs font-bold rounded">FINAL</span>
                                <h4 className="font-bold text-emerald-800">Conclusion prête à envoyer</h4>
                              </div>
                              <button
                                onClick={() => navigator.clipboard.writeText(conclusionResult.final!)}
                                className="btn-secondary text-xs flex items-center gap-1"
                              >
                                <Copy className="h-3 w-3" />
                                Copier
                              </button>
                            </div>

                            <div className="p-6 rounded-xl bg-white border-2 border-emerald-300 prose prose-sm max-w-none shadow-inner">
                              <div className="whitespace-pre-wrap text-slate-800 leading-relaxed">
                                {conclusionResult.final}
                              </div>
                            </div>
                          </div>

                          {/* Document complet avec conclusion */}
                          <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-300">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-blue-600" />
                                <h4 className="font-bold text-blue-800">Document complet</h4>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    // Générer PDF avec jsPDF
                                    const pdf = new jsPDF();
                                    const pageWidth = pdf.internal.pageSize.getWidth();
                                    const margin = 20;
                                    const maxWidth = pageWidth - margin * 2;
                                    let yPosition = 20;

                                    // Texte original
                                    pdf.setFontSize(10);
                                    const originalLines = pdf.splitTextToSize(result?.text || "", maxWidth);
                                    for (const line of originalLines) {
                                      if (yPosition > 270) {
                                        pdf.addPage();
                                        yPosition = 20;
                                      }
                                      pdf.text(line, margin, yPosition);
                                      yPosition += 5;
                                    }

                                    // Séparateur
                                    yPosition += 10;
                                    if (yPosition > 250) {
                                      pdf.addPage();
                                      yPosition = 20;
                                    }
                                    pdf.setLineWidth(0.5);
                                    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
                                    yPosition += 15;

                                    // CONCLUSION en gras
                                    pdf.setFontSize(14);
                                    pdf.setFont("helvetica", "bold");
                                    pdf.text("CONCLUSION", margin, yPosition);
                                    yPosition += 10;

                                    // Texte conclusion
                                    pdf.setFontSize(10);
                                    pdf.setFont("helvetica", "normal");
                                    const conclusionLines = pdf.splitTextToSize(conclusionResult.final || "", maxWidth);
                                    for (const line of conclusionLines) {
                                      if (yPosition > 270) {
                                        pdf.addPage();
                                        yPosition = 20;
                                      }
                                      pdf.text(line, margin, yPosition);
                                      yPosition += 5;
                                    }

                                    pdf.save(getBilanDownloadFilename("pdf"));
                                  }}
                                  className="btn-primary text-xs flex items-center gap-1"
                                >
                                  <FileOutput className="h-3 w-3" />
                                  PDF
                                </button>
                                <button
                                  onClick={async () => {
                                    // Générer Word avec docx
                                    const originalParagraphs = (result?.text || "").split("\n").map(line =>
                                      new Paragraph({
                                        children: [new TextRun({ text: line, size: 22 })],
                                      })
                                    );

                                    const conclusionParagraphs = (conclusionResult.final || "").split("\n").map(line =>
                                      new Paragraph({
                                        children: [new TextRun({ text: line, size: 22 })],
                                      })
                                    );

                                    const doc = new Document({
                                      sections: [{
                                        properties: {},
                                        children: [
                                          ...originalParagraphs,
                                          new Paragraph({ children: [] }), // Ligne vide
                                          new Paragraph({ children: [] }), // Ligne vide
                                          new Paragraph({
                                            children: [
                                              new TextRun({ text: "CONCLUSION", bold: true, size: 28 })
                                            ],
                                            heading: HeadingLevel.HEADING_1,
                                          }),
                                          new Paragraph({ children: [] }), // Ligne vide
                                          ...conclusionParagraphs,
                                        ],
                                      }],
                                    });

                                    const blob = await Packer.toBlob(doc);
                                    saveAs(blob, getBilanDownloadFilename("docx"));
                                  }}
                                  className="btn-secondary text-xs flex items-center gap-1 bg-blue-100 hover:bg-blue-200 text-blue-700"
                                >
                                  <Download className="h-3 w-3" />
                                  Word
                                </button>
                              </div>
                            </div>
                            <p className="text-xs text-blue-600">
                              Document original + CONCLUSION en gras
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Extracted Text Preview */}
          <div className="card overflow-hidden">
            <button
              onClick={() => toggleSection("original")}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-500" />
                <h3 className="text-lg font-bold text-slate-900">Texte extrait (original)</h3>
                <span className="badge-neutral text-xs">{result.text.length} car.</span>
              </div>
              <ChevronDown className={cn(
                "h-5 w-5 text-slate-400 transition-transform duration-200",
                expandedSections.original && "rotate-180"
              )} />
            </button>
            {expandedSections.original && (
              <div className="px-6 pb-6">
                <div className="max-h-60 overflow-y-auto rounded-xl bg-slate-50/80 p-4 font-mono text-xs text-slate-700 whitespace-pre-wrap">
                  {result.text.substring(0, 3000)}
                  {result.text.length > 3000 && (
                    <span className="text-slate-400">... (tronqué)</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Journal du processus (logs JSON téléchargeables) */}
          {processLog.hasSteps && (
            <div className="card overflow-hidden border-2 border-slate-200 bg-gradient-to-br from-slate-50/80 to-white">
              <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-slate-100 text-slate-600">
                    <FileJson className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Journal du processus</h3>
                    <p className="text-xs text-slate-500">
                      Succès, erreurs, durée, IA utilisée, tokens, entrées/sorties à chaque étape — pour optimisation et débogage
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={processLog.downloadLog}
                  className="btn-primary flex items-center gap-2 shrink-0"
                >
                  <Download className="h-4 w-4" />
                  Télécharger le journal (JSON)
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
